// [S-260 + S-262] Ingestion status — read-only document pipeline view.
// Lists every document Firestore has, grouped by lifecycle state.
// Failed documents expose a "Replay from stage 1" action (S-262)
// — platform_admin only at the server.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, PageHead, Select } from "../components";
import {
  DOCUMENT_LIFECYCLE_STATES,
  useIngestionDocuments,
  useReplayDocument,
  type DocumentLifecycleState,
  type IngestionDocumentRow,
} from "../data/ingestionQuery";
import { useTenantsList } from "../data/tenantsQuery";
import { useIdentityLabels } from "../data/identitiesQuery";

const STATE_OPTIONS: ReadonlyArray<{
  value: DocumentLifecycleState;
  label: string;
}> = DOCUMENT_LIFECYCLE_STATES.map((s) => ({ value: s, label: prettyState(s) }));

export function Ingestion(): JSX.Element {
  const [stateFilter, setStateFilter] = useState<DocumentLifecycleState | undefined>(
    undefined
  );
  const query = useIngestionDocuments({
    ...(stateFilter !== undefined ? { state: stateFilter } : {}),
    limit: 250,
  });

  const docs = query.data ?? [];
  const counts = useMemo(() => {
    const buckets: Record<DocumentLifecycleState, number> = {
      received: 0,
      parsing: 0,
      stage1_complete: 0,
      stage2_review: 0,
      stage3_human: 0,
      indexed: 0,
      failed: 0,
    };
    for (const d of docs) buckets[d.lifecycleState] += 1;
    return buckets;
  }, [docs]);

  return (
    <>
      <PageHead
        eyebrow="Platform · ingestion status"
        title={
          <>
            Document <em>pipeline.</em>
          </>
        }
        sub={
          <>
            Every contract Firestore has, newest first. Filter by lifecycle
            state to focus the view. {query.isLoading ? "Loading…" : `${docs.length} document${docs.length === 1 ? "" : "s"} shown.`}
          </>
        }
        cta={
          <Link to="/queue" className="btn btn--g btn--s">
            Open review queue →
          </Link>
        }
      />

      <div className="ingest__counts">
        {DOCUMENT_LIFECYCLE_STATES.map((s) => (
          <Button
            key={s}
            variant="bare"
            className={`ingest__bucket ingest__bucket--${s}${
              stateFilter === s ? " is-on" : ""
            }`}
            onClick={() =>
              setStateFilter((prev) => (prev === s ? undefined : s))
            }
            data-testid={`ingest-bucket-${s}`}
          >
            <span className="ingest__bucket__num">{counts[s]}</span>
            <span className="ingest__bucket__lbl">{prettyState(s)}</span>
          </Button>
        ))}
      </div>

      <div className="ingest__filter">
        <span className="ingest__filter__lbl">State</span>
        <Select
          label="Lifecycle state"
          value={stateFilter}
          options={STATE_OPTIONS}
          onValueChange={(v) => setStateFilter(v)}
          triggerClassName="ingest__filter__select"
        />
        {stateFilter !== undefined && (
          <Button
            variant="ghost"
            className="btn--xs"
            onClick={() => setStateFilter(undefined)}
          >
            Clear
          </Button>
        )}
      </div>

      {(query.isLoading || (query.isFetching && docs.length === 0)) && (
        <div className="ingest__state" role="status">
          Loading documents…
        </div>
      )}
      {query.isError && (
        <Alert label="Couldn't load" tone="error">
          {query.error?.message ?? "unknown error"}
        </Alert>
      )}
      {!query.isLoading && !query.isFetching && !query.isError && docs.length === 0 && (
        <div className="ingest__state" data-testid="ingest-empty">
          {stateFilter === undefined
            ? "No documents in Firestore yet. Upload one from /upload to see it appear here."
            : `No documents in state '${prettyState(stateFilter)}'.`}
        </div>
      )}

      {!query.isLoading && !query.isError && docs.length > 0 && (
        <DocumentTable rows={docs} />
      )}
    </>
  );
}

function DocumentTable({ rows }: { rows: IngestionDocumentRow[] }): JSX.Element {
  const replay = useReplayDocument();
  const studioNameByTenantId = new Map(
    (useTenantsList().data?.tenants ?? []).map((t) => [t.id, t.studioId])
  );
  const uploaderLabels = useIdentityLabels(rows.map((d) => d.uploadedBy)).data;
  return (
    <div className="ingest__tbl" role="table" aria-label="Documents">
      <div className="ingest__row ingest__row--head" role="row">
        <div role="columnheader" className="ingest__cell ingest__cell--id">
          Document
        </div>
        <div role="columnheader" className="ingest__cell">Organization</div>
        <div role="columnheader" className="ingest__cell">State</div>
        <div role="columnheader" className="ingest__cell">Uploaded</div>
        <div role="columnheader" className="ingest__cell">By</div>
        <div role="columnheader" className="ingest__cell ingest__cell--act">
          Actions
        </div>
      </div>
      {rows.map((d) => (
        <div
          className="ingest__row"
          key={d.id}
          role="row"
          data-testid={`ingest-row-${d.id}`}
        >
          <div role="cell" className="ingest__cell ingest__cell--id">
            {d.filename ?? "Unnamed document"}
          </div>
          <div role="cell" className="ingest__cell">
            {studioNameByTenantId.get(d.tenantId) ?? "—"}
          </div>
          <div role="cell" className="ingest__cell">
            <StatePill state={d.lifecycleState} />
          </div>
          <div role="cell" className="ingest__cell">
            {formatTs(d.uploadedAt)}
          </div>
          <div role="cell" className="ingest__cell">
            {uploaderLabels?.get(d.uploadedBy) ?? "—"}
          </div>
          <div role="cell" className="ingest__cell ingest__cell--act">
            {d.lifecycleState === "failed" && (
              <Button
                variant="secondary"
                className="btn--xs"
                onClick={() =>
                  replay.mutate({ documentId: d.id, fromStage: "1" })
                }
                disabled={
                  replay.isPending && replay.variables?.documentId === d.id
                }
                data-testid={`ingest-replay-${d.id}`}
              >
                {replay.isPending && replay.variables?.documentId === d.id
                  ? "Replaying…"
                  : "Replay"}
              </Button>
            )}
          </div>
        </div>
      ))}
      {replay.isError && (
        <Alert label="Replay failed" tone="error">
          {replay.error?.message ?? "unknown error"}
        </Alert>
      )}
    </div>
  );
}

function StatePill({ state }: { state: DocumentLifecycleState }): JSX.Element {
  return (
    <span className={`ingest__pill ingest__pill--${state}`}>
      {prettyState(state)}
    </span>
  );
}

function prettyState(s: DocumentLifecycleState): string {
  switch (s) {
    case "received":
      return "Received";
    case "parsing":
      return "Parsing";
    case "stage1_complete":
      return "Stage 1 done";
    case "stage2_review":
      return "Stage 2 review";
    case "stage3_human":
      return "Stage 3 human";
    case "indexed":
      return "Indexed";
    case "failed":
      return "Failed";
  }
}

function formatTs(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
